import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { createId } from '@core/util/file';
import { ICON } from '@core/util/icon';
import {
	GetCondoUnitTitleDetailsFromPin,
	GetParcelUnitTitleDetailsFromPin,
	getLatestInstrumentNumber,
} from '@core/util/legalDescription';
import {
	CondoUnit,
	MatterPropertyType,
	MatterTitle,
	UnitType,
} from '@models/matter';
import { SavedPin } from '@models/teranet';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import {
	TitleSyncPropertyTypeWarningModalComponent,
	TitleSyncPropertyTypeWarningModalData,
} from './title-sync-property-type-warning-modal/title-sync-property-type-warning-modal.component';
import { skip, take } from 'rxjs/operators';
import { MatterDispatch } from '@state/matter';
import { Observable } from 'rxjs';
import { Select } from '@ngxs/store';
import { TestHooks } from './title-sync-form.hooks';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
	selector: 'app-title-sync-form',
	templateUrl: './title-sync-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TitleSyncFormComponent implements OnInit {
	testHooks = TestHooks;
	@SelectSnapshot(MatterTitlesState.getAll) titles: MatterTitle[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@ViewSelectSnapshot(SelectedMatter.isCondoUnit) isCondoUnit: boolean;
	@Select(SelectedMatter.propertyType)
	propertyType$: Observable<MatterPropertyType>;

	@Input() set titleSearchPin(pin: SavedPin) {
		if (pin) {
			this.originalPin = pin;

			this.title = this.isCondoUnit
				? GetCondoUnitTitleDetailsFromPin(pin)
				: GetParcelUnitTitleDetailsFromPin(pin);
			this.latestInstrument = getLatestInstrumentNumber(pin);

			this.isLegalDescriptionCondoUnit = !!(
				(this.title as CondoUnit)?.levelNumber ||
				(this.title as CondoUnit)?.unitNumber
			);

			this.matchingTitle = this.titles.find(
				(title) => title.pinNumber === this.originalPin.pin,
			);
			if (this.matchingTitle) {
				// needs to set the id of the title so it can update the existing title with the same pin
				this.title.id = this.matchingTitle.id;
			}
		}
	}

	@Output() onTitleUpdate = new EventEmitter<MatterTitle>();

	public originalPin: SavedPin;
	public title: MatterTitle;
	public matchingTitle: MatterTitle;
	public latestInstrument: string;
	public isLegalDescriptionCondoUnit: boolean;

	@Input() set showComparison(comparison) {
		this._showComparison = !comparison;
	}

	public _showComparison: boolean = false;
	public ICON = ICON;
	public unitTypes = Object.values(UnitType);

	constructor(
		private matDialog: MatDialog,
		private matterDispatch: MatterDispatch,
	) {}

	ngOnInit() {
		this.toggleComparison(true);
	}

	toggleComparison(skipValidation = false) {
		// If unit number or level number is present, then we know they are trying to sync a condo unit
		if (this.canToggleComparison() && !skipValidation) {
			this.matDialog
				.open(TitleSyncPropertyTypeWarningModalComponent, {
					data: {
						isLegalDescriptionCondoUnit: this.isLegalDescriptionCondoUnit,
					} as TitleSyncPropertyTypeWarningModalData,
				})
				.afterClosed()
				.pipe(take(1), untilDestroyed(this))
				.subscribe((propertyType) => {
					if (propertyType) {
						this.propertyType$.pipe(skip(1), take(1)).subscribe(() => {
							this.toggleComparison();
						});
						this.matterDispatch.SetPropertyType(this.matterId, propertyType);
					}
				});
		} else {
			this._showComparison = !this._showComparison;
			this.updateTitle();
		}
	}

	canToggleComparison() {
		return (
			(!this.isLegalDescriptionCondoUnit && this.isCondoUnit) ||
			(this.isLegalDescriptionCondoUnit && !this.isCondoUnit)
		);
	}

	updateTitle() {
		this.onTitleUpdate.emit(
			this._showComparison
				? {
						...this.title,
						primary:
							(this.title as CondoUnit)?.unitType === UnitType.DWELLING_UNIT
								? true
								: false,
						id: this.title?.id ?? createId(),
					}
				: null,
		);
	}

	toggleSync($event: MouseEvent) {
		$event.preventDefault();
		this.toggleComparison();
	}
}
